import { useState, useRef, useEffect } from "react";
import { createSupportRequest } from "../api/services/supportService";
import supportBg from "../images/support-hero.webp";
import {
  Phone,
  Mail,
  Clock,
  ChevronDown,
  CircleQuestionMark,
  ShieldCheck,
  ArrowRight
} from "lucide-react";

const faqs = [
  {
    id: 1,
    question: "What is RentGurus and how does it work?",
    answer:
      "RentGurus is an online platform that lets you search, compare, and book rental vehicles from established rental suppliers worldwide.\n\nWe act as an intermediary. We arrange and confirm your reservation, while the vehicle is provided directly by the supplier you choose.\n\nAt the rental desk, you sign the agreement directly with the supplier and collect your vehicle.",
  },
  {
    id: 2,
    question: "How do I search and book a car on RentGurus?",
    answer:
      "Enter your pick-up location, dates, and times on our website.\n\nBrowse available vehicles, review the rental conditions for your chosen vehicle, and complete your booking online.\n\nYou will receive a confirmation email immediately once your booking is confirmed.",
  },
  {
    id: 3,
    question:
      'What does "partially prepaid" mean? What do I pay now vs. at the desk?',
    answer:
      "This is a two-step payment process. You pay a small upfront amount at the time of booking to secure your reservation. When you arrive at the rental desk to collect the vehicle, you pay the remaining rental balance, any optional extras, local fees, and the security deposit directly to the supplier.",
  },
  {
    id: 4,
    question: "How do I cancel my booking?",
    answer:
      "You can cancel your booking through the Manage Booking section on our website, or via the link in your confirmation email.\n\nFor further assistance, contact our support team at support@rentgurus.com.",
  },
  {
    id: 5,
    question: "Can I add an additional driver? Is there a fee?",
    answer:
      "Whether additional drivers are permitted depends on the supplier.\n\nWhere permitted, all additional drivers must meet the same age and licence requirements as the main driver and must be present at pick-up.\n\nAn additional driver fee may apply per day, payable at the rental desk.\n\nSome suppliers do not permit additional drivers at all. Check the rental conditions at checkout.",
  },
  {
    id: 6,
    question: "Where do I pick up the car at the airport?",
    answer:
      "The supplier name and pick-up location details are included in your confirmation email and rental voucher.\n\nAt most airports, rental desks are located in the arrivals terminal or a dedicated car rental centre. Follow the airport signage for 'Car Rental'.\n\nIf you are unable to locate the desk, contact the supplier directly using the contact details on your voucher.",
  },
];

const FAQItem = ({ faq, isOpen, onToggle }) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setHeight(contentRef.current.scrollHeight);
    } else {
      setHeight(0);
    }
  }, [isOpen]);

  return (
    <div
      className="border-b border-gray-200 last:border-b-0"
      onClick={onToggle}
    >
      <button className="w-full flex items-center justify-between px-5 py-4 text-left bg-white">
        <span className="font-body text-md font-medium text-black">
          {faq.question}
        </span>
        <ChevronDown
          size={16}
          className={`text-gray-400 flex-shrink-0 ml-3 transition-transform duration-500 ease-in-out ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
        />
      </button>

      <div
        style={{
          height: `${height}px`,
          overflow: "hidden",
          transition: "height 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div ref={contentRef} className="px-5 pb-4 bg-white">
          <p className="font-body text-sm text-gray-600 leading-relaxed">
            {faq.answer}
          </p>
        </div>
      </div>
    </div>
  );
};

const Support = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    bookingRef: "",
    message: "",
  });

  const [openId, setOpenId] = useState(null);
  const toggle = (id) => {
    setOpenId(openId === id ? null : id);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "fullName" && /\d/.test(value)) return;

    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    const payload = {
      full_name: formData.fullName,
      email_address: formData.email,
      reservation_number: formData.bookingRef,
      customer_message: formData.message,
    };

    setIsLoading(true);
    try {
      const response = await createSupportRequest(payload);
      console.log("API Response:", response);

      if (response.success) {
        setIsSubmitted(true);
        setFormData({ fullName: "", email: "", bookingRef: "", message: "" });
      } else {
        const code = response.validation_code;
        if (code === "INVALID_RESERVATION") {
          setErrors({ bookingRef: response.message });
        } else if (code === "EMAIL_MISMATCH") {
          setErrors({ email: response.message });
        }
      }
    } catch (error) {
      console.error("Unexpected error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required.";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email address is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address.";
    }

    if (!formData.bookingRef.trim()) {
      newErrors.bookingRef = "Booking reference is required.";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Please tell us how we can help.";
    } else if (formData.message.trim().length < 20) {
      newErrors.message = "Message must be at least 20 characters.";
    } else if (formData.message.trim().length > 500) {
      newErrors.message = "Message cannot exceed 500 characters.";
    }

    return newErrors;
  };

  return (
    <div className="w-full">
      {/* ── Hero Section ── */}
      <section
        className="relative w-full min-h-[340px] md:min-h-[380px] bg-black overflow-hidden mt-16"
        style={{
          backgroundImage: `url(${supportBg})`,
          backgroundSize: "cover",
          backgroundPosition: isMobile ? "35% 20%" : "100px 20%",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, #14222d 0%, #14222d 45%, #405759 60%, #587471 78%, #90bda8 100%)",
            opacity: 0.85,
          }}
        />
        {/* dark-to-transparent gradient overlay (left side readable) */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-24 pb-32">
          {/* Badge */}
          <div className="inline-flex items-center gap-1.5 border border-white/40 rounded-full px-3 py-1 mb-6">
            <CircleQuestionMark size={12} className="text-white" />
            <span className="font-heading text-xs font-semibold tracking-widest text-white uppercase">
              Help Centre
            </span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-5xl font-heading font-bold text-white leading-tight max-w-2xl mb-4 tracking-normal">
            We're here whenever you need us.
          </h1>

          {/* Sub-copy */}
          <p className="font-body text-white/80 text-lg md:text-lg max-w-xl leading-relaxed">
            Real humans, every hour of the day. Reach our team by phone or
            email, or browse the answers to the questions we hear most often.
          </p>
        </div>
      </section>

      {/* ── Contact Cards (overlap hero) ── */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 -mt-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Call card */}
          <div className="bg-white rounded-2xl shadow-lg px-6 py-6 flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
              <Phone size={20} className="text-primary" />
            </div>
            <div>
              <p className="font-heading text-[12px] font-semibold text-gray-600 uppercase mb-1">
                Call Us
              </p>
              <p className="font-heading text-xl font-extrabold text-black mb-1">
                +1 (888) 555–0199
              </p>
              <div className="flex items-center gap-1.5">
                <Clock size={13} className="text-primary" />
                <span className="font-body text-xs text-gray-500">
                  Toll-free · Available 24/7
                </span>
              </div>
            </div>
          </div>

          {/* Email card */}
          <div className="bg-white rounded-2xl shadow-lg px-6 py-6 flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
              <Mail size={20} className="text-primary" />
            </div>
            <div>
              <p className="font-heading text-[12px] font-semibold text-gray-600 uppercase mb-1">
                Email Us
              </p>
              <p className="font-heading text-xl font-extrabold text-black mb-1">
                support@rentgurus.com
              </p>
              <div className="flex items-center gap-1.5">
                <Clock size={13} className="text-primary" />
                <span className="font-body text-xs text-gray-500">
                  Replies within 2 hours
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ + Contact Form ── */}
      <section className="max-w-screen-xl mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[53%_47%] gap-12 lg:gap-8">
          {/* LEFT — FAQ */}
          <div>
            <p className="font-body text-xs font-semibold text-primary uppercase mb-2">
              Help Articles
            </p>
            <h2 className="font-heading text-3xl font-extrabold text-black mb-2">
              Frequently asked questions
            </h2>
            <p className="font-body text-sm text-gray-400 mb-7">
              Quick answers to the questions our renters ask most.
            </p>

            <div className="border border-gray-200 rounded-2xl overflow-y-auto">
              {faqs.map((faq) => (
                <FAQItem
                  key={faq.id}
                  faq={faq}
                  isOpen={openId === faq.id}
                  onToggle={() => toggle(faq.id)}
                />
              ))}
            </div>
            <div className="mt-6">
              <a
                href="/faqs"
                className="text-primary hover:text-primary/80 font-medium flex items-center gap-1.5 text-[14px] tracking-wide transition-colors duration-200 font-heading"
              >
                See more questions
                <ArrowRight size={16} className="translate-y-[0.5px]" />
              </a>
            </div>
          </div>

          {/* RIGHT — Contact Form */}
          <div>
            <p className="font-body text-xs font-semibold text-primary uppercase mb-2">
              Still Need Help?
            </p>
            <h2 className="font-heading text-3xl font-extrabold text-black mb-2">
              Send us a message
            </h2>
            <p className="font-heading text-sm text-gray-400 mb-7">
              Fill out the form and our team will get back to you within 2
              hours.
            </p>

            {isSubmitted ? (
              /* ── Success State ── */
              <div className="border border-gray-200 rounded-2xl p-6">
                <div className="bg-primary/5 border border-primary/20 rounded-xl px-5 py-5 flex items-start gap-4">
                  <ShieldCheck
                    size={22}
                    className="text-primary flex-shrink-0 mt-0.5"
                  />
                  <div>
                    <p className="font-heading text-sm font-semibold text-black mb-1">
                      Message received.
                    </p>
                    <p className="font-heading text-sm text-gray-500">
                      We'll get back to you shortly at the email you provided.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              /* ── Form ── */
              <div className="border border-gray-200 rounded-2xl p-6">
                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  {/* Full Name */}
                  <div>
                    <label className="font-heading text-xs font-semibold text-gray-500 uppercase block mb-1.5">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      placeholder="Jane Doe"
                      value={formData.fullName}
                      onChange={handleChange}
                      className={`w-full font-body text-sm border rounded-lg px-4 py-2.5 placeholder-gray-400 focus:outline-none transition-colors ${
                        errors.fullName
                          ? "border-red-400 focus:border-red-400"
                          : "border-gray-200 focus:border-primary"
                      }`}
                    />
                    {errors.fullName && (
                      <p className="font-body text-xs text-red-500 mt-1">
                        {errors.fullName}
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="font-heading text-xs font-semibold text-gray-500 uppercase block mb-1.5">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder="you@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full font-body text-sm border rounded-lg px-4 py-2.5 placeholder-gray-400 focus:outline-none transition-colors ${
                        errors.email
                          ? "border-red-400 focus:border-red-400"
                          : "border-gray-200 focus:border-primary"
                      }`}
                    />
                    {errors.email && (
                      <p className="font-body text-xs text-red-500 mt-1">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Booking Reference */}
                  <div>
                    <label className="font-heading text-xs font-semibold text-gray-500 uppercase block mb-1.5">
                      Reservation Number
                    </label>
                    <input
                      type="text"
                      name="bookingRef"
                      placeholder="e.g. FK10030008"
                      value={formData.bookingRef}
                      onChange={handleChange}
                      className={`w-full font-body text-sm border rounded-lg px-4 py-2.5 placeholder-gray-400 focus:outline-none transition-colors ${
                        errors.bookingRef
                          ? "border-red-400 focus:border-red-400"
                          : "border-gray-200 focus:border-primary"
                      }`}
                    />
                    {errors.bookingRef && (
                      <p className="font-body text-xs text-red-500 mt-1">
                        {errors.bookingRef}
                      </p>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <label className="font-heading text-xs font-semibold text-gray-500 uppercase block mb-1.5">
                      How Can We Help?
                    </label>
                    <textarea
                      name="message"
                      rows={5}
                      placeholder="Tell us a bit about your question…"
                      value={formData.message}
                      onChange={handleChange}
                      className={`w-full font-body text-sm border rounded-lg px-4 py-2.5 placeholder-gray-400 focus:outline-none transition-colors resize-y ${
                        errors.message
                          ? "border-red-400 focus:border-red-400"
                          : "border-gray-200 focus:border-primary"
                      }`}
                    />
                    <div className="flex items-center justify-between mt-1">
                      {errors.message ? (
                        <p className="font-body text-xs text-red-500">
                          {errors.message}
                        </p>
                      ) : (
                        <span />
                      )}
                      <p
                        className={`font-body text-xs ml-auto ${
                          formData.message.length > 500
                            ? "text-red-500"
                            : "text-gray-400"
                        }`}
                      >
                        {formData.message.length}/500
                      </p>
                    </div>
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-primary text-white font-heading font-semibold text-sm rounded-lg py-3 hover:opacity-90 active:opacity-80 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isLoading ? "Sending..." : "Send message"}
                  </button>

                  <p className="font-heading text-xs text-gray-400 text-center -mt-2">
                    By submitting, you agree to our{" "}
                    <a
                      href="/policy"
                      className="font-heading text-primary hover:underline transition-colors"
                    >
                      Privacy Policy
                    </a>
                    .
                  </p>
                </form>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Support;
